import {EPMethod, isEPCustomLike} from '../../../rendering/render.def';
import {getCustomerPhotoAccessibleName} from '../customer-photos/customer-photos-util';

export const THUMB_SWIPER_ID = 'prod-swiper-th';
export const ZOOM_SWIPER_ID = 'prod-swiper-th-zoom';
export const ZOOM_MODAL_ID = 'modal-prod-swiper-th';

const PORTAL_3 = 3;
const GLASS_EXCLUDED_PRODUCT_IDS = new Set([84054, 70214]);
const AUTO_THUMB_17_TYPES = new Set([83, 84]);
const AUTO_THUMB_16_TYPES = new Set([83, 84, 87, 91]);
const TH5_SCALE_OVERLAY = 'Photo represents scale dimensions only (final art/colors may differ)';

const CONFIGURED_THUMB_SLOTS: ReadonlyArray<{key: string; index: number; requiresDynamicCustomization?: boolean}> = [
    {key: 'th1', index: 1},
    {key: 'th2', index: 2},
    {key: 'th3', index: 3},
    {key: 'th4', index: 4},
    {key: 'th5', index: 5},
    {key: 'th7', index: 7},
    {key: 'th8', index: 8},
    {key: 'th9', index: 9},
    {key: 'th10', index: 10, requiresDynamicCustomization: true},
];

export type ProductSlideKind = 'product' | 'moulding' | 'mouldingProfile' | 'insignia' | 'insignia2' | 'insigniaLegacy' | 'lino' | 'matCombo' | 'glass' | 'autoThumb' | 'configuredThumb' | 'tassel' | 'video' | 'realImage';

export type ProductSlideThumb = {
    url: string;
    alt: string;
    border?: boolean;
    backgroundImage?: string;
    imgStyle?: {objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'};
    showPlayOverlay?: boolean;
};

export type ProductSlideZoom = {type: 'product-render'} | {type: 'image'; src: string; alt: string; backgroundColor?: string; overlay?: string} | {type: 'video'; poster: string; srcHorizontal: string; srcVertical: string; hasHorizontal: boolean; hasVertical: boolean};

export type ProductSlide = {
    id: string;
    kind: ProductSlideKind;
    description: string;
    accessibleName: string;
    thumb: ProductSlideThumb;
    zoom: ProductSlideZoom;
};

export type ProductSlideThumbSlot = {
    value?: string | null;
    disabled?: boolean;
};

export type ProductSlideVideoInput = {
    id?: number | null;
    alt?: string | null;
    h?: boolean;
    v?: boolean;
};

export type ProductSlideRealImageInput = {
    id?: number | null;
    alt?: string | null;
};

export type ProductSlidesInput = {
    portalId?: number | null;
    cdnDomain?: string | null;
    imgDomain?: string | null;
    insigniaABOld?: boolean;
    glassExperimentHidesSlide?: boolean;
    prodInstText?: string | null;
    customizationMethod?: number | null;
    epMethodId?: number | null;
    productId?: number | null;
    productTypeId?: number | null;
    isCaf?: boolean;
    isFrame?: boolean;
    disableGlassUpgrades?: boolean;
    allowGlassId2?: number | null;
    isTasselCord?: boolean;
    imageThumb?: string | null;
    documentId?: number | null;
    extraEntityId?: number | null;
    oldInsigniaThumb?: string | null;
    oldInsigniaImage?: string | null;
    thAuto21?: string | null;
    thAuto216?: string | null;
    thAuto217?: string | null;
    thAuto22?: string | null;
    th5Special?: boolean;
    mouldingId?: number | null;
    mat1Id?: number | null;
    mat2Id?: number | null;
    insigniaId?: number | null;
    linoId?: number | null;
    linoBaseName?: string | null;
    mouldingHasZoom?: boolean;
    mouldingTh5?: boolean;
    mouldingTh6?: boolean;
    mouldingZoomPath?: string | null;
    mouldingZoomPathTh?: string | null;
    nameInsignia?: string | null;
    titleInsignia?: string | null;
    // Insignia 2: the second fixed corner mark of an Icon Founders Edition product (type 92)
    insignia2Id?: number | null;
    nameInsignia2?: string | null;
    titleInsignia2?: string | null;
    // Mat 3: the fixed third mat of a Dimensions Plus product (type 75)
    mat3Id?: number | null;
    titleMat3?: string | null;
    // Mat Overlay: the fixed primary-mat print of a Dimensions Plus product (type 75)
    matOverlayId?: number | null;
    nameMat1?: string | null;
    titleMatCombo?: string | null;
    titleMoulding?: string | null;
    colorMat1?: string | null;
    thumbs?: Record<string, ProductSlideThumbSlot | undefined> | null;
    videos?: Array<ProductSlideVideoInput | null | undefined> | null;
    realImages?: Array<ProductSlideRealImageInput | null | undefined> | null;
};

export type ZoomSwiperLike = {
    destroyed?: boolean;
    params?: {loop?: boolean};
    slideToLoop?: (index: number) => void;
    slideTo?: (index: number) => void;
};

const hasText = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

const positiveId = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

const asNumber = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

const openLarger = (description: string): string => `Open larger image of ${description}`;

const webpFrom = (src: string): string => src.replace('.png', '.webp').replace('.jpg', '.webp');

const matBackground = (cdn: string, nameMat1: string | null | undefined): string | undefined => {
    if (!hasText(cdn) || !hasText(nameMat1)) {
        return undefined;
    }
    return `image-set(url('https://${cdn}/images/entities/primarymat/v2/${nameMat1}_320.webp') type('image/webp') 1x, url('https://${cdn}/images/entities/primarymat/v2/${nameMat1}_320.jpg') type('image/jpeg') 1x)`;
};

const productThumbUrl = (input: ProductSlidesInput): string | null => {
    const method = asNumber(input.customizationMethod);
    const cdn = input.cdnDomain ?? '';
    const img = input.imgDomain ?? '';
    const mat2 = positiveId(input.mat2Id) ? input.mat2Id : 0;
    const insignia = positiveId(input.insigniaId) ? input.insigniaId : 0;
    const lino = positiveId(input.linoId) ? input.linoId : 0;

    if (method === 1) {
        if (!hasText(input.imageThumb)) {
            return null;
        }
        return input.imageThumb.includes('https') ? input.imageThumb.toLowerCase() : hasText(cdn) ? `https://${cdn}/images/products/thumb/${input.imageThumb}`.toLowerCase() : null;
    }

    if (method === 2) {
        if (!hasText(img)) {
            return null;
        }
        if (input.isCaf) {
            return `${img}/i/0_220_250_${asNumber(input.productId)}_${asNumber(input.mouldingId)}_${asNumber(input.mat1Id)}_${mat2}_${insignia}_${asNumber(input.documentId)}_${asNumber(input.extraEntityId)}_0___0____0__11.000_0_8.500_0_0_0_0.png`;
        }
        // Icon Founders Edition (type 92) carries its second corner mark (or -1) so a changed mark gets a new /i file and is drawn with the mark the URL names.
        const insignia2 = asNumber(input.productTypeId) === 92 ? `&Insignia2ID=${positiveId(input.insignia2Id) ? input.insignia2Id : -1}` : '';
        // Dimensions Plus (type 75) carries its fixed third mat (or -1) for the same reason: /i caches by the URL, so a changed mat gets a new file drawn with the mat the URL names.
        const mat3 = asNumber(input.productTypeId) === 75 ? `&Mat3ID=${positiveId(input.mat3Id) ? input.mat3Id : -1}` : '';
        // ... and its Mat Overlay (or -1), so an overlay assigned, changed or cleared gets a new file drawn with the overlay the URL names.
        const matOverlay = asNumber(input.productTypeId) === 75 ? `&MatOverlayID=${positiveId(input.matOverlayId) ? input.matOverlayId : -1}` : '';
        return `${img}/i/${asNumber(input.productId)}_220_250.png?MouldingID=${asNumber(input.mouldingId)}&PrimaryMatID=${asNumber(input.mat1Id)}&SecondaryMatID=${mat2}&InsigniaID=${insignia}&LinoID=${lino}${insignia2}${mat3}${matOverlay}`;
    }

    return null;
};

const allowPortalAutoMedia = (portalId: number): boolean => portalId !== PORTAL_3;

const seenPush = (slides: ProductSlide[], usedIds: Set<string>, slide: ProductSlide | null): void => {
    if (!slide || usedIds.has(slide.id)) {
        return;
    }
    usedIds.add(slide.id);
    slides.push(slide);
};

/**
 * Ordered product-page media. Thumbnail and zoom carousels must render this
 * array in this order; zoom fields may differ in size/encoding only.
 *
 * Generic `th6` is omitted on purpose (the moulding profile slide is the th6 view).
 * `th_auto_2_2` is not its own slide; it only replaces th5's zoom URL when th5Special.
 */
export function buildProductSlides(input: ProductSlidesInput): ProductSlide[] {
    const slides: ProductSlide[] = [];
    const usedIds = new Set<string>();
    const portalId = asNumber(input.portalId);
    const cdn = hasText(input.cdnDomain) ? input.cdnDomain : '';
    const method = asNumber(input.customizationMethod);
    const epMethodId = asNumber(input.epMethodId);
    const productTypeId = asNumber(input.productTypeId);
    const canUseCdn = hasText(cdn);

    const showProductSlide = (method === 1 || method === 2) && !isEPCustomLike(epMethodId) && epMethodId !== EPMethod.SingleDoc;
    if (showProductSlide) {
        const url = productThumbUrl(input);
        if (hasText(url)) {
            seenPush(slides, usedIds, {
                id: 'slide-prod',
                kind: 'product',
                description: 'product',
                accessibleName: openLarger('product'),
                thumb: {url, alt: 'Product', border: true},
                zoom: {type: 'product-render'},
            });
        }
    }

    if (positiveId(input.mouldingId) && canUseCdn && (input.isCaf || (allowPortalAutoMedia(portalId) && input.mouldingHasZoom))) {
        const useTh5 = Boolean(input.isCaf || input.mouldingTh5);
        const thumbFile = useTh5 ? `${input.mouldingId}_th5_75.png` : input.mouldingZoomPathTh;
        const zoomFile = useTh5 ? `${input.mouldingId}_th5.png` : input.mouldingZoomPath;
        if (hasText(thumbFile) && hasText(zoomFile)) {
            const zoomSrc = `https://${cdn}/images/entities/moulding/${useTh5 ? zoomFile : zoomFile.toLowerCase()}`;
            seenPush(slides, usedIds, {
                id: 'slide-prod-moulding',
                kind: 'moulding',
                description: 'corner view of moulding',
                accessibleName: openLarger('corner view of moulding'),
                thumb: {url: `https://${cdn}/images/entities/moulding/${thumbFile}`, alt: 'Moulding'},
                zoom: {type: 'image', src: zoomSrc, alt: hasText(input.titleMoulding) ? input.titleMoulding : 'Moulding'},
            });
        }
    }

    if (positiveId(input.mouldingId) && canUseCdn && (input.isCaf || (allowPortalAutoMedia(portalId) && input.mouldingTh6))) {
        seenPush(slides, usedIds, {
            id: 'slide-prod-moulding-2',
            kind: 'mouldingProfile',
            description: 'profile view of moulding',
            accessibleName: openLarger('profile view of moulding'),
            thumb: {url: `https://${cdn}/images/entities/moulding/${input.mouldingId}_th6_75.png`, alt: 'Moulding Profile'},
            zoom: {type: 'image', src: `https://${cdn}/images/entities/moulding/${input.mouldingId}_th6.png`, alt: hasText(input.titleMoulding) ? input.titleMoulding : 'Moulding Profile'},
        });
    }

    if (!input.insigniaABOld && positiveId(input.insigniaId) && canUseCdn && hasText(input.nameInsignia)) {
        const name = input.nameInsignia.toLowerCase();
        const title = hasText(input.titleInsignia) ? input.titleInsignia : 'Insignia';
        seenPush(slides, usedIds, {
            id: 'slide-prod-ins',
            kind: 'insignia',
            description: 'insignia',
            accessibleName: openLarger('insignia'),
            thumb: {
                url: `https://${cdn}/images/entities/insignia/v2/${name}_90.png`,
                alt: title,
                backgroundImage: matBackground(cdn, input.nameMat1),
            },
            zoom: {
                type: 'image',
                src: `https://${cdn}/images/entities/insignia/v2/${name}_original.png`,
                alt: title,
                backgroundColor: hasText(input.colorMat1) ? `#${input.colorMat1}` : undefined,
            },
        });
    }

    // Icon Founders Edition (type 92): the second fixed corner mark gets the same closeup slide as the insignia.
    if (positiveId(input.insignia2Id) && canUseCdn && hasText(input.nameInsignia2)) {
        const name = input.nameInsignia2.toLowerCase();
        const title = hasText(input.titleInsignia2) ? input.titleInsignia2 : 'Insignia 2';
        seenPush(slides, usedIds, {
            id: 'slide-prod-ins2',
            kind: 'insignia2',
            description: 'insignia 2',
            accessibleName: openLarger('insignia 2'),
            thumb: {
                url: `https://${cdn}/images/entities/insignia/v2/${name}_90.png`,
                alt: title,
                backgroundImage: matBackground(cdn, input.nameMat1),
            },
            zoom: {
                type: 'image',
                src: `https://${cdn}/images/entities/insignia/v2/${name}_original.png`,
                alt: title,
                backgroundColor: hasText(input.colorMat1) ? `#${input.colorMat1}` : undefined,
            },
        });
    }

    if (input.insigniaABOld && method !== 2 && canUseCdn && hasText(input.oldInsigniaThumb)) {
        const title = hasText(input.titleInsignia) ? input.titleInsignia : 'Insignia';
        const thumbUrl = `https://${cdn}/images/products/insignia/thumb/${input.oldInsigniaThumb.toLowerCase()}`;
        const zoomSrc = hasText(input.oldInsigniaImage) ? `https://${cdn}/images/products/insignia/full/${input.oldInsigniaImage.toLowerCase()}` : thumbUrl;
        seenPush(slides, usedIds, {
            id: 'slide-prod-old-ins',
            kind: 'insigniaLegacy',
            description: 'insignia',
            accessibleName: openLarger('insignia'),
            thumb: {url: thumbUrl, alt: title},
            zoom: {type: 'image', src: zoomSrc, alt: title},
        });
    }

    if (positiveId(input.linoId) && canUseCdn) {
        const thumbUrl = `https://${cdn}/images/entities/all/${input.linoId}_original.png`;
        const zoomSrc = hasText(input.linoBaseName) ? `https://${cdn}/images/entities/linotype_product/v2/${input.linoBaseName.replace(/ /g, '_')}_original.png`.replace('occupation_', '').toLowerCase() : thumbUrl;
        seenPush(slides, usedIds, {
            id: 'slide-prod-lino',
            kind: 'lino',
            description: 'lino',
            accessibleName: openLarger('lino'),
            thumb: {
                url: thumbUrl,
                alt: 'Lino',
                backgroundImage: matBackground(cdn, input.nameMat1),
                imgStyle: {objectFit: 'contain'},
            },
            zoom: {
                type: 'image',
                src: zoomSrc,
                alt: 'Lino',
                backgroundColor: hasText(input.colorMat1) ? `#${input.colorMat1}` : undefined,
            },
        });
    }

    if (positiveId(input.mat1Id) && canUseCdn) {
        const comboSuffix = `${input.mat1Id}${positiveId(input.mat2Id) ? `_${input.mat2Id.toString()}` : ''}`;
        // Dimensions Plus (type 75) names its fixed third mat after the combo, as the cart does; the image stays the two-mat combo (a three-mat variant is a follow-up)
        const mat3Title = asNumber(input.productTypeId) === 75 && positiveId(input.mat3Id) && hasText(input.titleMat3) ? ` / ${input.titleMat3}` : '';
        const title = `${hasText(input.titleMatCombo) ? input.titleMatCombo : 'Mats'}${mat3Title}`;
        seenPush(slides, usedIds, {
            id: 'slide-prod-matcombo',
            kind: 'matCombo',
            description: 'mats',
            accessibleName: openLarger('mats'),
            thumb: {url: `https://${cdn}/images/entities/matcombos/75_${comboSuffix}.png`, alt: title},
            zoom: {type: 'image', src: `https://${cdn}/images/entities/matcombos/1024_${comboSuffix}.png`, alt: title},
        });
    }

    let videoOrdinal = 0;
    for (const video of input.videos ?? []) {
        if (!video || !positiveId(video.id) || !canUseCdn) {
            continue;
        }
        videoOrdinal += 1;
        const description = hasText(video.alt) ? video.alt.trim() : `product video ${video.id}`;
        const accessibleName = hasText(video.alt) ? `Play ${video.alt.trim()}` : `Play product video ${videoOrdinal}`;
        seenPush(slides, usedIds, {
            id: `slide-prod-video-${video.id}`,
            kind: 'video',
            description,
            accessibleName,
            thumb: {
                url: `https://${cdn}/images/cached/rp/rp${video.id}_th250.jpg`,
                alt: hasText(video.alt) ? video.alt.trim() : 'Product Video',
                showPlayOverlay: true,
            },
            zoom: {
                type: 'video',
                poster: `https://${cdn}/images/cached/rp/rp${video.id}_th1024.jpg`,
                srcHorizontal: `https://${cdn}/images/cached/rp/rp${video.id}_h.mp4`,
                srcVertical: `https://${cdn}/images/cached/rp/rp${video.id}_v.mp4`,
                hasHorizontal: Boolean(video.h),
                hasVertical: Boolean(video.v),
            },
        });
    }

    const showGlass = !input.glassExperimentHidesSlide && positiveId(input.allowGlassId2) && Boolean(input.isFrame) && !GLASS_EXCLUDED_PRODUCT_IDS.has(asNumber(input.productId)) && !input.disableGlassUpgrades && canUseCdn;
    if (showGlass) {
        seenPush(slides, usedIds, {
            id: 'slide-prod-glassoptions',
            kind: 'glass',
            description: 'glass options',
            accessibleName: openLarger('glass options'),
            thumb: {url: `https://${cdn}/images/cached/general/glass-all-75.png`, alt: 'Glass Options'},
            zoom: {type: 'image', src: `https://${cdn}/images/cached/general/glass-all.png`, alt: 'Glass Options'},
        });
    }

    if (allowPortalAutoMedia(portalId) && hasText(input.thAuto21)) {
        seenPush(slides, usedIds, {
            id: 'slide-prod-th_auto_2_1',
            kind: 'autoThumb',
            description: 'product thumbnail #1',
            accessibleName: openLarger('product thumbnail #1'),
            thumb: {url: input.thAuto21, alt: 'Product thumbnail #1', border: true},
            zoom: {type: 'image', src: input.thAuto21, alt: 'Product thumbnail zoom #1'},
        });
    }

    if (AUTO_THUMB_17_TYPES.has(productTypeId) && allowPortalAutoMedia(portalId) && hasText(input.thAuto217)) {
        seenPush(slides, usedIds, {
            id: 'slide-prod-th_auto_2_17',
            kind: 'autoThumb',
            description: 'product thumbnail #17',
            accessibleName: openLarger('product thumbnail #17'),
            thumb: {url: input.thAuto217, alt: 'Product thumbnail #17', border: true},
            zoom: {type: 'image', src: input.thAuto217, alt: 'Product thumbnail zoom #17'},
        });
    }

    if (allowPortalAutoMedia(portalId) && input.isTasselCord && canUseCdn) {
        const tasselAlt = `Tassel & Cord Frame holds ${input.prodInstText ?? ''} graduation tassel or honor cords`;
        seenPush(slides, usedIds, {
            id: 'slide-prod-tassel-cord',
            kind: 'tassel',
            description: 'tassel cord',
            accessibleName: openLarger('tassel cord'),
            thumb: {url: `https://${cdn}/images/cached/general/tassel-cord-th.png`, alt: tasselAlt},
            zoom: {type: 'image', src: `https://${cdn}/images/cached/general/tassel-cord-2-way.png`, alt: tasselAlt},
        });
    }

    if (AUTO_THUMB_16_TYPES.has(productTypeId) && allowPortalAutoMedia(portalId) && hasText(input.thAuto216)) {
        seenPush(slides, usedIds, {
            id: 'slide-prod-th_auto_2_16',
            kind: 'autoThumb',
            description: 'product thumbnail #16',
            accessibleName: openLarger('product thumbnail #16'),
            thumb: {url: input.thAuto216, alt: 'Product thumbnail #16', border: true},
            zoom: {type: 'image', src: input.thAuto216, alt: 'Product thumbnail zoom #16'},
        });
    }

    for (const slot of CONFIGURED_THUMB_SLOTS) {
        if (slot.requiresDynamicCustomization && method !== 2) {
            continue;
        }
        const thumb = input.thumbs?.[slot.key];
        if (!thumb || thumb.disabled || !hasText(thumb.value) || !canUseCdn) {
            continue;
        }
        const description = `product thumbnail #${slot.index}`;
        const isTh5 = slot.key === 'th5';
        const specialZoom = isTh5 && input.th5Special && hasText(input.thAuto22) ? `https://${cdn}${input.thAuto22}` : null;
        seenPush(slides, usedIds, {
            id: `slide-prod-${slot.key}`,
            kind: 'configuredThumb',
            description,
            accessibleName: openLarger(description),
            thumb: {url: `https://${cdn}${thumb.value}_75.jpg`, alt: `Product thumbnail #${slot.index}`, border: true},
            zoom: {
                type: 'image',
                src: specialZoom ?? `https://${cdn}${thumb.value}_1440.jpg`,
                alt: `Product thumbnail zoom #${slot.index}`,
                overlay: isTh5 && !input.th5Special ? TH5_SCALE_OVERLAY : undefined,
            },
        });
    }

    let realOrdinal = 0;
    for (const image of input.realImages ?? []) {
        if (!image || !positiveId(image.id) || !canUseCdn) {
            continue;
        }
        realOrdinal += 1;
        const accessible = getCustomerPhotoAccessibleName(image.alt, realOrdinal);
        seenPush(slides, usedIds, {
            id: `slide-prod-real-${image.id}`,
            kind: 'realImage',
            description: accessible,
            accessibleName: openLarger(accessible),
            thumb: {url: `https://${cdn}/images/cached/rp/rp${image.id}_th250.jpg`, alt: accessible},
            zoom: {type: 'image', src: `https://${cdn}/images/cached/rp/rp${image.id}_th1024.jpg`, alt: accessible},
        });
    }

    return slides;
}

export function getProductSlideIds(slides: ReadonlyArray<Pick<ProductSlide, 'id'>>): string[] {
    return slides.map(slide => slide.id);
}

export function findSlideIndexById(slides: ReadonlyArray<Pick<ProductSlide, 'id'>>, id: string | null | undefined): number {
    if (!hasText(id)) {
        return -1;
    }
    return slides.findIndex(slide => slide.id === id);
}

export function getThumbnailSlidesPerView(width: number): number {
    if (!Number.isFinite(width)) {
        return 6;
    }
    if (width < 400) {
        return 3;
    }
    if (width < 510) {
        return 4;
    }
    if (width < 580) {
        return 5;
    }
    return 6;
}

/** Swiper min-width breakpoints matching getThumbnailSlidesPerView. Default slidesPerView is 3. */
export const THUMBNAIL_SWIPER_BREAKPOINTS = {
    400: {slidesPerView: 4},
    510: {slidesPerView: 5},
    580: {slidesPerView: 6},
};

export function shouldLoopThumbnails(slideCount: number, slidesPerView: number, customizationMethod: number): boolean {
    return slideCount > slidesPerView && customizationMethod >= 2;
}

export function shouldShowThumbnailNavigation(slideCount: number, slidesPerView: number): boolean {
    return slideCount > slidesPerView;
}

export function shouldLoopZoom(slideCount: number): boolean {
    return slideCount > 1;
}

export function webpSrc(src: string): string {
    return webpFrom(src);
}

export function pickZoomVideoSrc(zoom: Extract<ProductSlideZoom, {type: 'video'}>, viewportWidth: number, viewportHeight: number): string {
    const useHorizontal = zoom.hasHorizontal && (!zoom.hasVertical || viewportWidth >= viewportHeight);
    return useHorizontal ? zoom.srcHorizontal : zoom.srcVertical;
}

export function pauseVideosIn(root: {querySelectorAll: (selector: string) => ArrayLike<{pause: () => void}>} | null | undefined): void {
    if (!root || typeof root.querySelectorAll !== 'function') {
        return;
    }
    const videos = root.querySelectorAll('video');
    for (let i = 0; i < videos.length; i += 1) {
        try {
            videos[i]?.pause();
        } catch {
            // ignore media errors from detached nodes
        }
    }
}

export function navigateZoomSwiper(swiper: ZoomSwiperLike | null | undefined, index: number): boolean {
    if (!swiper || swiper.destroyed || !Number.isFinite(index) || index < 0) {
        return false;
    }
    try {
        if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(index);
            return true;
        }
        if (typeof swiper.slideTo === 'function') {
            swiper.slideTo(index);
            return true;
        }
    } catch {
        return false;
    }
    return false;
}

/** Jump to the opened slide once. Do not re-slide later — delayed slideToLoop fights the user's first prev/next. */
export function scheduleZoomNavigation(options: {swiper: ZoomSwiperLike | null | undefined; index: number; isVideo?: boolean; playVideo?: () => void; setTimeoutFn?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>}): Array<ReturnType<typeof setTimeout>> {
    const {swiper, index, isVideo, playVideo} = options;
    const delay: (handler: () => void, ms: number) => ReturnType<typeof setTimeout> = options.setTimeoutFn ?? ((handler, ms) => setTimeout(handler, ms) as ReturnType<typeof setTimeout>);
    const moved = navigateZoomSwiper(swiper, index);
    if (!moved || !isVideo) {
        return [];
    }
    return [
        delay(() => {
            if (!swiper?.destroyed) {
                playVideo?.();
            }
        }, 250),
    ];
}

export function clearScheduledTimeouts(ids: Array<ReturnType<typeof setTimeout> | undefined | null>, clearTimeoutFn: (id: ReturnType<typeof setTimeout>) => void = id => clearTimeout(id as Parameters<typeof clearTimeout>[0])): void {
    for (const id of ids) {
        if (id != null) {
            clearTimeoutFn(id);
        }
    }
}
